import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { BBPS_CATEGORIES, PAYOUT_RAILS } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Package as PackageIcon, Pencil, Trash2, Users } from "lucide-react";

function defaultFeeRow() {
  return { platform_fee_flat: 0, platform_fee_percent: 0, partner_margin_flat: 0, partner_margin_percent: 0 };
}

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, object = edit
  const [assigning, setAssigning] = useState(null); // package to assign

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        api.get("/admin/price-packages"),
        api.get("/admin/users"),
      ]);
      setPackages(p.data || []);
      setPartners((u.data || []).filter((x) => x.role === "partner"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    const cat = {};
    BBPS_CATEGORIES.forEach((c) => (cat[c.id] = defaultFeeRow()));
    const pay = {};
    PAYOUT_RAILS.forEach((r) => (pay[r.id] = defaultFeeRow()));
    setEditing({ name: "", description: "", is_default: false, category_fees: cat, payout_fees: pay });
  };

  const openEdit = (pkg) => setEditing({ ...pkg });

  const savePackage = async () => {
    if (!editing.name.trim()) { toast.error("Package name is required"); return; }
    try {
      if (editing.id) {
        await api.put(`/admin/price-packages/${editing.id}`, editing);
        toast.success("Package updated");
      } else {
        await api.post("/admin/price-packages", editing);
        toast.success("Package created");
      }
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
  };

  const deletePkg = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/admin/price-packages/${id}`);
      toast.success("Package deleted");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };

  const assign = async (partnerId) => {
    try {
      await api.post(`/admin/partners/${partnerId}/assign-package`, { package_id: assigning.id });
      toast.success("Package assigned");
      setAssigning(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Assign failed");
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Admin</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Price packages &amp; fee config
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Define per-category platform fees and partner margin. Assign packages to partners
            individually.
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-package-btn">
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New package
        </Button>
      </div>

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="packages-grid">
        {loading ? (
          <div className="text-muted-foreground">Loading…</div>
        ) : packages.length === 0 ? (
          <div className="col-span-full surface-card p-12 text-center">
            <PackageIcon className="h-6 w-6 mx-auto text-muted-foreground" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">No packages yet.</p>
          </div>
        ) : (
          packages.map((p) => {
            const partnerCount = partners.filter((x) => x.price_package_id === p.id).length;
            return (
              <div key={p.id} data-testid={`pkg-${p.id}`} className="surface-card p-5 interactive-card">
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-md bg-accent text-primary grid place-items-center">
                    <PackageIcon className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  {p.is_default && <Badge>Default</Badge>}
                  {p.validity_days > 0 && <Badge variant="outline">{p.validity_days}d validity</Badge>}
                </div>
                <div className="font-display text-lg font-bold mt-3">{p.name}</div>
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.description || "—"}</p>
                <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {partnerCount} partner{partnerCount === 1 ? "" : "s"}
                </div>
                <div className="mt-5 flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-pkg-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setAssigning(p)} data-testid={`assign-pkg-${p.id}`}>
                    Assign
                  </Button>
                  {!p.is_default && (
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deletePkg(p.id)} data-testid={`delete-pkg-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </Button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Editor dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit package" : "New package"}</DialogTitle>
            <DialogDescription>
              Fee = flat + % of transaction amount. Platform fee is debited from partner wallet.
              Partner margin is what the partner charges the consumer.
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input data-testid="pkg-name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1" />
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <Switch
                    id="is-default"
                    checked={!!editing.is_default}
                    onCheckedChange={(v) => setEditing({ ...editing, is_default: v })}
                    data-testid="pkg-default"
                  />
                  <Label htmlFor="is-default" className="cursor-pointer">Set as default (new partners)</Label>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="pkg-description" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="mt-1" rows={2} />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Validity (days)</Label>
                  <Input
                    data-testid="pkg-validity-days"
                    type="number"
                    min="0"
                    placeholder="0 = no expiry"
                    value={editing.validity_days || ""}
                    onChange={(e) => setEditing({ ...editing, validity_days: parseInt(e.target.value) || 0 })}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground mt-1">0 or blank = never expires. E.g. 30, 90, 365.</p>
                </div>
              </div>

              <FeeGrid
                title="BBPS categories"
                items={BBPS_CATEGORIES}
                fees={editing.category_fees || {}}
                onChange={(id, field, val) => {
                  const next = { ...(editing.category_fees || {}) };
                  next[id] = { ...(next[id] || defaultFeeRow()), [field]: parseFloat(val) || 0 };
                  setEditing({ ...editing, category_fees: next });
                }}
                testid="pkg-cat"
              />
              <FeeGrid
                title="Payout rails"
                items={PAYOUT_RAILS}
                fees={editing.payout_fees || {}}
                onChange={(id, field, val) => {
                  const next = { ...(editing.payout_fees || {}) };
                  next[id] = { ...(next[id] || defaultFeeRow()), [field]: parseFloat(val) || 0 };
                  setEditing({ ...editing, payout_fees: next });
                }}
                testid="pkg-rail"
              />

              <PgFeasibilityEditor
                allowedGateways={editing.allowed_gateways || []}
                allowedChannels={editing.allowed_pg_channels || {}}
                onGatewaysChange={(arr) => setEditing({ ...editing, allowed_gateways: arr })}
                onChannelsChange={(obj) => setEditing({ ...editing, allowed_pg_channels: obj })}
              />

              <TopupFeesEditor
                value={editing.topup_fees || {}}
                adjustments={editing.topup_fee_adjustments || {}}
                onChange={(next) => setEditing({ ...editing, topup_fees: next })}
                onAdjustmentsChange={(next) => setEditing({ ...editing, topup_fee_adjustments: next })}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={savePackage} data-testid="save-package">Save package</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign dialog */}
      <Dialog open={assigning !== null} onOpenChange={(o) => !o && setAssigning(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Assign “{assigning?.name}” to partners</DialogTitle>
            <DialogDescription>Click a partner to switch their package.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Partner</TableHead>
                  <TableHead>Current package</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium">{p.full_name}</div>
                      <div className="text-xs text-muted-foreground">{p.email}</div>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {packages.find((x) => x.id === p.price_package_id)?.name || "— none —"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant={p.price_package_id === assigning?.id ? "secondary" : "outline"}
                        disabled={p.price_package_id === assigning?.id}
                        onClick={() => assign(p.id)}
                        data-testid={`assign-to-${p.id}`}
                      >
                        {p.price_package_id === assigning?.id ? "Assigned" : "Assign"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Retailer charge caps */}
      <RetailerChargeCaps />
    </DashboardLayout>
  );
}

function FeeGrid({ title, items, fees, onChange, testid }) {
  return (
    <div>
      <span className="overline">{title}</span>
      <div className="mt-3 surface-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead className="w-24">Fee flat ₹</TableHead>
              <TableHead className="w-20">Fee %</TableHead>
              <TableHead className="w-24">Margin flat ₹</TableHead>
              <TableHead className="w-20">Margin %</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => {
              const f = fees[it.id] || defaultFeeRow();
              return (
                <TableRow key={it.id}>
                  <TableCell className="font-medium">{it.label}</TableCell>
                  <TableCell>
                    <Input
                      type="number" min="0" step="any"
                      data-testid={`${testid}-${it.id}-fee-flat`}
                      value={f.platform_fee_flat ?? 0}
                      onChange={(e) => onChange(it.id, "platform_fee_flat", e.target.value)}
                      className="h-8 font-mono text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number" min="0" step="any"
                      data-testid={`${testid}-${it.id}-fee-pct`}
                      value={f.platform_fee_percent ?? 0}
                      onChange={(e) => onChange(it.id, "platform_fee_percent", e.target.value)}
                      className="h-8 font-mono text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number" min="0" step="any"
                      data-testid={`${testid}-${it.id}-margin-flat`}
                      value={f.partner_margin_flat ?? 0}
                      onChange={(e) => onChange(it.id, "partner_margin_flat", e.target.value)}
                      className="h-8 font-mono text-xs"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number" min="0" step="any"
                      data-testid={`${testid}-${it.id}-margin-pct`}
                      value={f.partner_margin_percent ?? 0}
                      onChange={(e) => onChange(it.id, "partner_margin_percent", e.target.value)}
                      className="h-8 font-mono text-xs"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


// ─── PG feasibility editor ──────────────────────────────────────────────────
// Lists supported gateways and (for ManaRecharge) live PG channels with checkboxes.
// Stores `allowed_gateways: string[]` and `allowed_pg_channels: {gateway: string[]}`
// on the package. NULL/[] = deny-all (per product decision).
const SUPPORTED_GATEWAYS = [
  { key: "manarecharge", label: "ManaRecharge", supports_channel: true },
  { key: "razorpay", label: "Razorpay", supports_channel: false },
  { key: "cashfree", label: "Cashfree", supports_channel: false },
  { key: "runpaisa", label: "Runpaisa", supports_channel: false },
];

function PgFeasibilityEditor({ allowedGateways, allowedChannels, onGatewaysChange, onChannelsChange }) {
  const [manaChannels, setManaChannels] = useState([]);
  const [loadingChs, setLoadingChs] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingChs(true);
      try {
        const { data } = await api.get("/admin/payment-links/pg-channels");
        setManaChannels(data.channels || []);
      } catch {
        setManaChannels([]);
      } finally {
        setLoadingChs(false);
      }
    })();
  }, []);

  const toggleGateway = (key) => {
    const set = new Set(allowedGateways);
    if (set.has(key)) set.delete(key);
    else set.add(key);
    onGatewaysChange(Array.from(set));
  };

  const toggleChannel = (gw, ch) => {
    const next = { ...(allowedChannels || {}) };
    const list = new Set(next[gw] || []);
    if (list.has(ch)) list.delete(ch);
    else list.add(ch);
    next[gw] = Array.from(list);
    onChannelsChange(next);
  };

  return (
    <div data-testid="pkg-pg-feasibility">
      <div className="flex items-baseline justify-between mb-3">
        <h4 className="font-display font-semibold text-sm">Payment gateway feasibility</h4>
        <span className="text-[10px] text-muted-foreground">
          Default: deny-all. Tick to enable per package.
        </span>
      </div>
      <div className="rounded-lg border bg-muted/10 divide-y">
        {SUPPORTED_GATEWAYS.map((g) => {
          const gwEnabled = (allowedGateways || []).includes(g.key);
          const gwChannels = (allowedChannels || {})[g.key] || [];
          return (
            <div key={g.key} className="p-3" data-testid={`pkg-gateway-${g.key}`}>
              <div className="flex items-center justify-between">
                <Label className="cursor-pointer flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={gwEnabled}
                    onChange={() => toggleGateway(g.key)}
                    className="h-4 w-4 accent-primary"
                    data-testid={`toggle-gateway-${g.key}`}
                  />
                  <span className="font-semibold text-sm">{g.label}</span>
                </Label>
                {g.supports_channel && gwEnabled && (
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {gwChannels.length} channel{gwChannels.length === 1 ? "" : "s"} enabled
                  </span>
                )}
              </div>
              {g.supports_channel && gwEnabled && (
                <div className="mt-2 ml-6">
                  {loadingChs ? (
                    <div className="text-xs text-muted-foreground">Loading channels…</div>
                  ) : manaChannels.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">
                      Could not load live channels — check ManaRecharge connectivity.
                    </div>
                  ) : (
                    <div className="grid grid-cols-4 gap-1.5">
                      {manaChannels.map((ch) => {
                        const enabled = gwChannels.includes(ch.id);
                        return (
                          <label
                            key={ch.id}
                            className={`flex items-center gap-1.5 rounded border px-2 py-1 cursor-pointer text-[11px] font-mono transition-colors ${
                              enabled ? "border-primary bg-primary/5" : "border-border hover:bg-accent/40"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={enabled}
                              onChange={() => toggleChannel(g.key, ch.id)}
                              className="h-3 w-3 accent-primary"
                              data-testid={`toggle-channel-${g.key}-${ch.id}`}
                            />
                            <span>{ch.id}</span>
                            {!ch.active && (
                              <Badge variant="outline" className="ml-auto h-3.5 px-1 text-[9px]">
                                off
                              </Badge>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function RetailerChargeCaps() {
  const [caps, setCaps] = useState({ max_flat: 0, max_pct: 0, category_overrides: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/bbps/v2/admin/retailer-charge-caps");
        setCaps(data || { max_flat: 0, max_pct: 0, category_overrides: {} });
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/bbps/v2/admin/retailer-charge-caps", caps);
      toast.success("Retailer charge caps saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mt-8 surface-card p-5" data-testid="retailer-charge-caps-section">
      <h3 className="font-display text-lg font-semibold">Retailer charge caps</h3>
      <p className="text-sm text-muted-foreground mt-1">Maximum retailer charge allowed per BBPS transaction. Partners cannot exceed these limits.</p>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div>
          <Label>Max flat charge (₹)</Label>
          <Input type="number" min="0" value={caps.max_flat} onChange={(e) => setCaps({ ...caps, max_flat: parseFloat(e.target.value) || 0 })} className="font-mono mt-1" data-testid="cap-max-flat" />
          <p className="text-[11px] text-muted-foreground mt-1">0 = no flat cap</p>
        </div>
        <div>
          <Label>Max percentage (%)</Label>
          <Input type="number" min="0" max="100" step="0.1" value={caps.max_pct} onChange={(e) => setCaps({ ...caps, max_pct: parseFloat(e.target.value) || 0 })} className="font-mono mt-1" data-testid="cap-max-pct" />
          <p className="text-[11px] text-muted-foreground mt-1">0 = no percentage cap</p>
        </div>
      </div>
      <Button className="mt-4" onClick={save} disabled={saving} data-testid="cap-save-btn">
        {saving ? "Saving..." : "Save caps"}
      </Button>
    </div>
  );
}

// ─── Top-up fees per gateway (per-package override of master) ──────────────
// Supports the same nested-channel format used by AdminTopupFees:
//   { gateway: { channel: { mdr_flat, mdr_percent, settle_flat, settle_percent } } }
// Legacy flat shape { gateway: { mdr_flat, ... } } is auto-normalised to
// { gateway: { default: {...} } } on load and de-normalised back on save.
const TOPUP_GATEWAYS = [
  { key: "manarecharge", label: "ManaRecharge", supports_channel: true },
  { key: "razorpay", label: "Razorpay", supports_channel: false },
  { key: "cashfree", label: "Cashfree", supports_channel: false },
  { key: "runpaisa", label: "Runpaisa", supports_channel: false },
];
const TOPUP_KEYS = ["mdr_flat", "mdr_percent", "settle_flat", "settle_percent"];
const TOPUP_LABELS = {
  mdr_flat: "MDR ₹", mdr_percent: "MDR %", settle_flat: "Settle ₹", settle_percent: "Settle %",
};
const _topup_blank = () => ({ mdr_flat: 0, mdr_percent: 0, settle_flat: 0, settle_percent: 0 });

function _normalizeTopupValue(val) {
  const out = {};
  for (const [gw, entry] of Object.entries(val || {})) {
    if (!entry || typeof entry !== "object") continue;
    if (TOPUP_KEYS.some((k) => k in entry)) {
      // Legacy flat → wrap as { default: rule }.
      out[gw] = { default: { ..._topup_blank(), ...entry } };
    } else {
      const channels = {};
      for (const [ch, rule] of Object.entries(entry)) {
        channels[ch] = { ..._topup_blank(), ...(rule || {}) };
      }
      out[gw] = channels;
    }
  }
  return out;
}

function _denormalizeTopupValue(state) {
  const out = {};
  for (const [gw, channels] of Object.entries(state)) {
    if (!channels || Object.keys(channels).length === 0) continue;
    const meta = TOPUP_GATEWAYS.find((g) => g.key === gw);
    const keys = Object.keys(channels);
    if (!meta?.supports_channel && keys.length === 1 && keys[0] === "default") {
      out[gw] = channels.default;
    } else {
      out[gw] = channels;
    }
  }
  return out;
}

function TopupFeesEditor({ value, adjustments, onChange, onAdjustmentsChange }) {
  // `value` = legacy override (deprecated, kept for backward compat — read-only display).
  // `adjustments` = the new primary input. Signed deltas applied on top of admin master.
  const [adjState, setAdjState] = React.useState(() => _normalizeTopupValue(adjustments));
  const [masterFees, setMasterFees] = React.useState({});
  const [masterLoading, setMasterLoading] = React.useState(false);

  React.useEffect(() => {
    setAdjState(_normalizeTopupValue(adjustments));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(adjustments)]);

  React.useEffect(() => {
    let cancel = false;
    setMasterLoading(true);
    api.get("/admin/topup-fees/master").then((res) => {
      if (cancel) return;
      setMasterFees(_normalizeTopupValue(res.data?.fees || {}));
    }).catch(() => { /* keep empty — admin not configured master yet */ })
      .finally(() => { if (!cancel) setMasterLoading(false); });
    return () => { cancel = true; };
  }, []);

  const emitAdj = (next) => {
    setAdjState(next);
    onAdjustmentsChange(_denormalizeTopupValue(next));
  };

  const hasLegacyOverride = value && Object.keys(value).length > 0;

  const toggleGateway = (gw, enabled) => {
    const next = { ...adjState };
    if (enabled) next[gw] = next[gw] || { default: _topup_blank() };
    else delete next[gw];
    emitAdj(next);
  };

  const setCell = (gw, ch, k, v) => {
    const parsed = parseFloat(v);
    emitAdj({
      ...adjState,
      [gw]: {
        ...(adjState[gw] || {}),
        [ch]: {
          ...((adjState[gw] || {})[ch] || _topup_blank()),
          [k]: Number.isFinite(parsed) ? parsed : 0,
        },
      },
    });
  };

  const addChannel = (gw, raw) => {
    const id = (raw || "").trim();
    if (!id || !/^[a-z0-9_-]+$/.test(id)) {
      toast.error("Channel id must be lowercase letters/digits/underscore (e.g. pay0, pay3)");
      return false;
    }
    if ((adjState[gw] || {})[id]) {
      toast.error(`Channel '${id}' already exists`);
      return false;
    }
    emitAdj({
      ...adjState,
      [gw]: { ...(adjState[gw] || {}), [id]: _topup_blank() },
    });
    return true;
  };

  const removeChannel = (gw, ch) => {
    if (ch === "default") {
      toast.error("Cannot remove 'default' — uncheck the gateway instead.");
      return;
    }
    const channels = { ...(adjState[gw] || {}) };
    delete channels[ch];
    emitAdj({ ...adjState, [gw]: channels });
  };

  // Read the master fee for a given (gw, channel), with channel→default fallback.
  const masterFor = (gw, ch) => {
    const gwEntry = masterFees[gw] || {};
    return gwEntry[ch] || gwEntry.default || null;
  };

  return (
    <div data-testid="topup-fees-editor">
      <div className="overline">Top-up fee adjustments (discount / extra charges)</div>
      <p className="text-xs text-muted-foreground mt-1">
        Adjusts the admin master PG fees specifically for partners on this package.
        Enter a <b className="text-destructive">negative</b> value to give a discount,
        or <b className="text-success">positive</b> to add an extra charge. Final fee
        is always clamped ≥ 0. Leave a gateway off to charge the master fee as-is.
        {masterLoading && <span className="ml-1 italic">· loading master…</span>}
      </p>

      {hasLegacyOverride && (
        <div className="mt-2 text-[11px] rounded-md border border-amber-300/40 bg-amber-50/40 px-2 py-1.5 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          ⚠ This package has a <b>legacy full override</b> on {Object.keys(value).join(", ")}.
          The override still applies (takes precedence over adjustments + master).
          Clear it via API or save this package once to migrate.
        </div>
      )}

      <div className="mt-3 space-y-2">
        {TOPUP_GATEWAYS.map((g) => {
          const enabled = !!adjState[g.key];
          const channels = adjState[g.key] || {};
          const masterMissing = !masterFees[g.key];
          return (
            <div key={g.key} className="rounded-md border border-border p-3" data-testid={`pkg-topup-${g.key}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="inline-flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    data-testid={`pkg-topup-toggle-${g.key}`}
                    checked={enabled}
                    onChange={(e) => toggleGateway(g.key, e.target.checked)}
                    disabled={masterMissing}
                  />
                  {g.label}
                  {g.supports_channel && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20 font-mono">
                      multi-channel
                    </span>
                  )}
                </label>
                {masterMissing ? (
                  <span className="text-[10px] text-muted-foreground italic">
                    no master fee — configure at /admin/topup-fees first
                  </span>
                ) : enabled && (
                  <span className="text-[10px] text-muted-foreground font-mono">
                    {Object.keys(channels).length} channel{Object.keys(channels).length === 1 ? "" : "s"}
                  </span>
                )}
              </div>
              {enabled && !masterMissing && (
                <div className="mt-3 space-y-2">
                  {Object.entries(channels).map(([ch, adjRule]) => (
                    <AdjustmentRow
                      key={ch}
                      gw={g.key}
                      channel={ch}
                      adjRule={adjRule}
                      masterRule={masterFor(g.key, ch)}
                      onCellChange={(k, v) => setCell(g.key, ch, k, v)}
                      onRemove={() => removeChannel(g.key, ch)}
                      canRemove={ch !== "default"}
                    />
                  ))}
                  {g.supports_channel && (
                    <PkgAddChannel onAdd={(id) => addChannel(g.key, id)} gw={g.key} />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// One row = master read-only + signed adjustment input + computed final preview.
function AdjustmentRow({ gw, channel, adjRule, masterRule, onCellChange, onRemove, canRemove }) {
  const compute = (k) => {
    const m = Number(masterRule?.[k] || 0);
    const a = Number(adjRule?.[k] || 0);
    return Math.max(0, +(m + a).toFixed(4));
  };
  return (
    <div className="rounded border border-border bg-muted/10 p-2.5" data-testid={`pkg-topup-${gw}-${channel}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-background border border-border font-semibold">
            {channel}
          </span>
          {channel === "default" && (
            <span className="text-[10px] text-muted-foreground italic">fallback channel</span>
          )}
          {!masterRule && (
            <span className="text-[10px] text-destructive font-medium">
              · no master for this channel — adjustment will have no effect
            </span>
          )}
        </div>
        {canRemove && (
          <button
            type="button"
            className="text-[11px] text-destructive hover:underline"
            onClick={onRemove}
            data-testid={`pkg-remove-channel-${gw}-${channel}`}
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-4 gap-2">
        {TOPUP_KEYS.map((k) => {
          const m = Number(masterRule?.[k] || 0);
          const a = Number(adjRule?.[k] || 0);
          const final = compute(k);
          const tone = a < 0 ? "text-success" : a > 0 ? "text-destructive" : "text-muted-foreground";
          return (
            <div key={k} className="space-y-0.5">
              <div className="text-[10px] text-muted-foreground">{TOPUP_LABELS[k]}</div>
              <div className="text-[10px] text-muted-foreground font-mono" data-testid={`pkg-topup-${gw}-${channel}-master-${k}`}>
                master: <b>{m}</b>
              </div>
              <input
                type="number"
                step={k.endsWith("percent") ? "0.01" : "0.5"}
                value={a}
                onChange={(e) => onCellChange(k, e.target.value)}
                data-testid={`pkg-topup-${gw}-${channel}-adj-${k}`}
                placeholder="±0"
                className={`w-full font-mono text-sm rounded-md border border-input bg-background px-2 py-1 ${tone}`}
              />
              <div className="text-[10px] font-mono" data-testid={`pkg-topup-${gw}-${channel}-final-${k}`}>
                final: <b className={final === 0 && (m + a) < 0 ? "text-success" : ""}>{final}</b>
                {a !== 0 && (
                  <span className={`ml-1 ${tone}`}>
                    ({a > 0 ? "+" : ""}{a})
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PkgAddChannel({ onAdd, gw }) {
  const [val, setVal] = React.useState("");
  return (
    <div className="flex items-center gap-2 pt-0.5">
      <input
        placeholder="e.g. pay0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="font-mono text-[12px] h-7 max-w-[140px] rounded-md border border-input bg-background px-2"
        data-testid={`pkg-add-channel-input-${gw}`}
      />
      <button
        type="button"
        onClick={() => { if (onAdd(val)) setVal(""); }}
        className="text-[11px] px-2 py-0.5 rounded border border-border hover:bg-accent inline-flex items-center gap-1"
        data-testid={`pkg-add-channel-btn-${gw}`}
      >
        <Plus className="h-3 w-3" />
        Add channel
      </button>
    </div>
  );
}

